import L from "leaflet";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Polygon, TileLayer, useMap } from "react-leaflet";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import {
  GEO_VISION_API_URL,
  LayerAlias,
  LAYERS,
  MapModes,
  STORAGE_URL,
  UnitTypes
} from "constant";
import { useMapContext } from "context/MapContextProvider";
import { findMinMaxRescale } from "helpers/findMinMaxRescale";
import { getValuesByKeys } from "helpers/getValuesByKeys";
import { getToken } from "helpers/jwt";
import { RootStateInterface } from "reducer";
import { LayersManagerStateInterface } from "scenes/LayersManager/interfaces";
import usePolygonHandler from "scenes/MapPage/usePolygonHandler";
import { UnitsManagerStateInterface } from "scenes/UnitsManager/interfaces";
import { MapStateInterface } from "../../interfaces";

import MapMarkers from "../MapMarkers/MapMarkers";
import MapPhotoMarkers from "../MapPhotoMarkers/MapPhotoMarkers";
import MapPolygonContextMenu from "../MapPolygonContextMenu/MapPolygonContextMenu";
import MapPolygons from "../MapPolygons/MapPolygons";

const clusteringZoom = 20;

interface MapLayersItemsInterface {
  mapZoom: {
    zoom: number;
    maxNativeZoom: number;
  };
}

const MapLayersItems = ({ mapZoom }: MapLayersItemsInterface) => {
  const map = useMap();

  const { id } = useParams();
  const { openContextMenu } = useMapContext();

  const [isClustering, setIsClustering] = useState(
    map.getZoom() < clusteringZoom
  );

  const { aries, markerDetails, mapMode } = useSelector<
    RootStateInterface,
    MapStateInterface
  >((state) => state.map);

  const { layers } = useSelector<
    RootStateInterface,
    LayersManagerStateInterface
  >((state) => state.layersManager);

  const { unitsCollection, creationPolygonParameters, classifications } =
    useSelector<RootStateInterface, UnitsManagerStateInterface>(
      (state) => state.unitsManager
    );

  const { openPolygonDrawMode, setActivePolygonsListItem } = usePolygonHandler(
    creationPolygonParameters
  );

  const selectedLayers = useMemo(() => {
    return layers.filter((layer) => layer.isActive);
  }, [layers]);

  const selectedAries = useMemo(
    () => aries.filter((aria) => aria.isActive),
    [aries]
  );

  const markersWithHazardZone = useMemo(() => {
    const activeLayersAliases = selectedLayers
      .map((el) => el.alias)
      .filter((item) => item !== LayerAlias.THERMAL);

    const availableMarkers = getValuesByKeys(unitsCollection, [
      ...activeLayersAliases
    ])
      .map((layer) => layer[UnitTypes.POINT].units)
      .flat();

    return availableMarkers;
  }, [unitsCollection, selectedLayers]);

  const magneticLayer = useMemo(() => {
    return selectedLayers.find((layer) => layer.alias === LayerAlias.MAGNETIC);
  }, [selectedLayers]);
  const thermalLayer = useMemo(() => {
    return selectedLayers.find((layer) => layer.alias === LayerAlias.THERMAL);
  }, [selectedLayers]);

  const onPolygonRightClick = useCallback(
    (areaData) => {
      if (mapMode === MapModes.POLYGON_CREATION) {
        openPolygonDrawMode(areaData);
      }
    },
    [openPolygonDrawMode, mapMode]
  );

  const availablePolygons = useMemo(
    () =>
      unitsCollection[LayerAlias.MANUAL][UnitTypes.POLYGON].units
        ?.filter((el) => el.isVisible && el.id !== creationPolygonParameters.id)
        .map((el, index) => {
          // You need to use a long key that will include all the polygon parameters that can change.
          const key = `${index}-${el.additional.stroke_color}-${el.additional.fill_color}-${el.additional.stroke_transparency}-${el.additional.fill_transparency}`;

          return (
            <Polygon
              positions={el.geo_data.coordinates}
              key={key}
              color={el.additional.stroke_color}
              fillColor={el.additional.fill_color}
              opacity={el.additional.stroke_transparency}
              fillOpacity={el.additional.fill_transparency}
              eventHandlers={{
                contextmenu: (e) => {
                  const containerPoint = e.containerPoint;

                  onPolygonRightClick(el);

                  setActivePolygonsListItem(el);

                  mapMode === MapModes.DEFAULT &&
                    setTimeout(() => {
                      //A timeout is needed to show the current data of the polygon we click on. Due to the click event handler that is running in parallel in the MapEventHandler component, we cannot do this as usual.
                      openContextMenu({
                        position: { x: containerPoint.x, y: containerPoint.y },
                        customMenu: <MapPolygonContextMenu areaData={el} />
                      });
                    }, 0);
                }
              }}
            />
          );
        }),
    [
      creationPolygonParameters.id,
      onPolygonRightClick,
      unitsCollection,
      setActivePolygonsListItem,
      openContextMenu,
      mapMode
    ]
  );

  const buildTilesLink = useCallback(
    ({
      layerAlias,
      layerActiveFilter,
      providerId,
      providerFilters,
      providerRescaleMin,
      providerRescaleMax
    }) => {
      let filterParams = "";

      if (layerActiveFilter) {
        const selectedFilter = providerFilters?.find(
          (item) => item.formula === layerActiveFilter.name
        );

        const { rescale_min, rescale_max } = findMinMaxRescale(
          layerActiveFilter.rescalePercents,
          selectedFilter?.statistics?.percentiles?.[0] ??
            providerRescaleMin ??
            0,
          selectedFilter?.statistics?.percentiles?.[1] ??
            providerRescaleMax ??
            255
        );
        const updatedFilter = {
          ...selectedFilter,
          rescale: `${rescale_min}, ${rescale_max}`,
          bands: "auto"
        };

        delete updatedFilter.rescale_min;
        delete updatedFilter.rescale_max;

        filterParams = Object.entries(updatedFilter)
          .map(([key, value]) => {
            if (updatedFilter.formula === "RGB") {
              if (key === "color_map") return "";
              if (key === "formula") return "";
            }
            return `${key}=${encodeURIComponent(value as string)}`;
          })
          .join("&");
      }

      return `${GEO_VISION_API_URL}/projects/${id}/layers/${
        layerAlias === LayerAlias.SENSOR_FUSION ? LayerAlias.RGB : layerAlias
      }/providers/${providerId}/tiles/{z}/{x}/{y}?${filterParams}`;
    },
    [id]
  );

  const getFilteredByClassificationsUnits = useCallback(
    (units) => {
      return units.filter(
        (el) => classifications[el?.additional?.classification?.alias]
      );
    },
    [classifications]
  );

  const allMarkers = useMemo(() => {
    const markersFromLayers = [
      LAYERS.manual,
      ...selectedLayers.filter((layer) => layer.alias !== LayerAlias.THERMAL)
    ];

    const markers = markersFromLayers.map((layer) =>
      getFilteredByClassificationsUnits(
        unitsCollection[layer.alias][UnitTypes.POINT].units
      ).map((marker) => {
        return {
          ...marker,
          layerAlias: layer.alias
        };
      })
    );

    return markers;
  }, [getFilteredByClassificationsUnits, selectedLayers, unitsCollection]);

  useEffect(() => {
    const handleZoom = () => {
      setIsClustering(map.getZoom() < clusteringZoom);
    };

    map.on("zoomend", handleZoom);
    return () => {
      map.off("zoomend", handleZoom);
    };
  }, [map]);

  return (
    <>
      <TileLayer
        url="https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
        maxNativeZoom={20}
        maxZoom={30}
      />
      {selectedLayers
        .filter(
          (layer) =>
            layer.alias !== LayerAlias.MAGNETIC &&
            layer.alias !== LayerAlias.THERMAL &&
            layer.alias !== LayerAlias.RGB2
        )
        .map((item) =>
          item?.providers
            ?.filter((provider) => provider.isActiveTiles ?? true)
            .map((provider) => (
              <TileLayer
                accessToken={getToken()}
                url={buildTilesLink({
                  layerAlias: item.alias,
                  layerActiveFilter: item.filter,
                  providerId: provider.id,
                  providerFilters: provider?.data.filters,
                  providerRescaleMin: provider?.data.rescale_min,
                  providerRescaleMax: provider?.data.rescale_max
                })}
                bounds={
                  provider.data?.bounds
                    ? new L.LatLngBounds([
                        [provider?.data?.bounds[1], provider?.data?.bounds[0]],
                        [provider?.data?.bounds[3], provider?.data?.bounds[2]]
                      ])
                    : null
                }
                maxNativeZoom={mapZoom.maxNativeZoom}
                maxZoom={30}
                opacity={(item.opacity ?? 100) / 100}
                detectRetina={true}
                key={`tile-${item.alias}-${provider.id}`}
                id={provider.data.name}
              />
            ))
        )}
      {allMarkers.map((markers, index) => (
        <MapMarkers
          markers={markers}
          activeMarkerId={markerDetails?.id}
          opacityByLayerList={selectedLayers.filter(
            (layer) => layer.alias === LayerAlias.THERMAL
          )}
          isClustering={isClustering}
          key={`${markerDetails?.id}:${index}`}
        />
      ))}

      {magneticLayer?.providers
        ?.filter((provider) => provider.isActiveTiles ?? true)
        .map((provider) => (
          <TileLayer
            url={`${STORAGE_URL}/mapbuilder/projects/${id}/magnetic/providers/${provider.id}/tiles/{z}/{x}/{y}.png`}
            maxNativeZoom={provider?.data?.maxzoom}
            minNativeZoom={provider?.data?.minzoom}
            maxZoom={30}
            opacity={(magneticLayer?.opacity ?? 100) / 100}
            key={`magnetic-provider-${provider.id}-tiles`}
            bounds={
              provider.data?.bounds
                ? new L.LatLngBounds([
                    [provider?.data?.bounds?.[1], provider?.data?.bounds?.[0]],
                    [provider?.data?.bounds?.[3], provider?.data?.bounds?.[2]]
                  ])
                : null
            }
          />
        ))}
      {thermalLayer?.providers
        ?.filter((provider) => provider.isActiveTiles ?? true)
        .map((provider) => (
          <TileLayer
            url={`${STORAGE_URL}/mapbuilder/projects/${id}/thermal/providers/${provider.id}/tiles/{z}/{x}/{y}.png`}
            maxNativeZoom={provider?.data?.maxzoom}
            minNativeZoom={provider?.data?.minzoom}
            maxZoom={30}
            opacity={(thermalLayer?.opacity ?? 100) / 100}
            key={`thermal-provider-${provider.id}-tiles`}
            bounds={
              provider.data?.bounds
                ? new L.LatLngBounds([
                    [provider?.data?.bounds[1], provider?.data?.bounds?.[0]],
                    [provider?.data?.bounds[3], provider?.data?.bounds?.[2]]
                  ])
                : null
            }
          />
        ))}
      {selectedAries?.length > 0 && markersWithHazardZone.length > 0 && (
        <MapPolygons aries={selectedAries} markers={markersWithHazardZone} />
      )}
      {/* {thermalMarkers.length > 0 && (
            <MapPolygons
              aries={[
                {
                  name: "Thermal Area",
                  color: selectedLayers.find(
                    (layer) => layer.alias === LayerAlias.THERMAL
                  ).color,
                  isActive: true,
                  alias: LayerAlias.THERMAL,
                  radius: 0.2
                }
              ]}
              markers={thermalMarkers}
            />
          )} */}
      {unitsCollection[LayerAlias.MANUAL][UnitTypes.POLYGON]?.units?.length >
        0 && availablePolygons}

      <MapPhotoMarkers
        photoMarkers={unitsCollection[LayerAlias.RGB2][UnitTypes.PHOTO]?.units}
      />
    </>
  );
};

export default MapLayersItems;
